import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import classNames from "classnames";
import React from "react";
import {useUserState} from "@stores/AppStateContext";

import {Notification} from "../../../../API";

import {NotificationActiveIcon} from "../../../components/icons/NotificationActiveIcon";
import {NotificationCard} from "./NotificationCard";
import {differenceInDays} from "date-fns";
import {isToday} from "date-fns/isToday";

import {screenSizeProvider} from "../../../../stores/ScreenSizeProvider";

import "./NotificationPanel.css"

export const NotificationPanel = observer(
    () => {
        const userState = useUserState();
        const notificationsDs = userState?.dsManager?.notificationsDS;
        if (!notificationsDs || !notificationsDs.isInitialized || !userState?.dsManager?.workspacesDS.isInitialized) {
            return 'loading';
        }

        const notifications = notificationsDs.filteredRows;

        const groupedNotifications = groupNotificationsByDate(notifications);
        const isMobile = screenSizeProvider.view !== 'desktop';
        return (
            <div className="NotificationPanel" key={isMobile ? 'mobile' : 'desktop'}>
                <div className={"NotificationPanel__wrapper title-medium-bold"}>
                    <div className="NotificationPanel__header__wrapper">
                        <div className={"NotificationPanel__header"}>
                            <div className="NotificationPanel__title">
                                {intl.get('notification.common').d('notifications')}
                            </div>
                            <div className={"NotificationPanel__actions title-small"}>
                            <span
                                className={classNames({
                                    "NotificationPanel__action": true,
                                    "NotificationPanel__action--active": notificationsDs.filterValues.status === 'all'
                                })}
                                onClick={() => notificationsDs.setFilterValue('status', "all")}
                            >
                                {intl.get('notification.all').d('All')}
                            </span>
                                <span className={classNames({
                                    "NotificationPanel__action": true,
                                    "NotificationPanel__action--active": notificationsDs.filterValues.status === 'unread'
                                })}
                                      onClick={() => notificationsDs.setFilterValue('status', "unread")}
                                >
                                    {intl.get('notification.unread').d('Unread')}
                                </span>
                            </div>
                        </div>
                        <div className={"NotificationPanel__divider"}></div>
                    </div>
                    <div className={classNames({
                        "NotificationPanel__notifications": true,
                        "NotificationPanel__notifications--empty": notifications.length < 1
                    })}>
                        {notifications.length > 0 ? (
                            <>
                                {
                                    groupedNotifications.map(({group, notifications}) => (
                                        <div key={group} className="NotificationPanel__group">
                                            <div className="NotificationPanel__group-title label-small">
                                                {groupName(group)}
                                            </div>
                                            {
                                                notifications.map(notification => (
                                                    <NotificationCard
                                                        key={notification.id}
                                                        notification={notification}
                                                    />
                                                ))
                                            }
                                        </div>
                                    ))
                                }
                            </>
                        ) : (
                            notificationsDs.filterValues.status === 'all' ? (
                                <>
                                    <NotificationActiveIcon size={24} className={"NotificationPanel__empty-icon"}/>
                                    <div className={"NotificationPanel__empty-title body-medium-bold"}>
                                        {intl.get('notification.empty.header').d('No notifications yet')}
                                    </div>
                                    <div className={"NotificationPanel__empty-text body-small"}>
                                        {intl.get('notification.empty.text').d("You'll receive updates on mentions, replies, and important changes.")}
                                    </div>
                                </>
                            ) : (
                                <div className={"NotificationPanel__empty-text body-medium"}>
                                    {intl.get('notification.unread.empty.text').d('You have no unread notifications.')}
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

interface GroupedNotifications {
    group: string;
    notifications: Notification[];
}

const groupNotificationsByDate = (notifications: Notification[]): GroupedNotifications[] => {
    const groups: Record<"today" | "last7days" | "older", Notification[]> = {
        today: [],
        last7days: [],
        older: [],
    };

    notifications.forEach(notification => {
        const date = notification.createdAt;

        if (isToday(date)) {
            groups.today.push(notification);
        } else if (differenceInDays(new Date(), date) <= 7) {
            groups.last7days.push(notification);
        } else {
            groups.older.push(notification);
        }
    });

    return Object.entries(groups)
        .filter(([, items]) => items.length > 0)
        .map(([key, notifications]) => ({
            group: key,
            notifications,
        }));
};

const groupName = (group: string) => {
    switch (group) {
        case "today":
            return intl.get("group_by_date.today").d("Today");
        case "last7days":
            return intl.get("group_by_date.week.ago").d("Last 7 days");
        case "older":
            return intl.get("group_by_date.older_then_7_days").d("Older");
        default:
            return group;
    }
};
